import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { NFCScanner } from "@/components/scan/PublicNFCScanner";
import { QRScanner } from "@/components/scan/PublicQRScanner";
import { AppleButton } from "@/components/ui/apple-button";
import { LottiePlayer } from "@/components/ui/lottie-player";
import AppLayout from "@/components/layout/AppLayout";
import { ArrowLeft, ArrowRight, QrCode, Download, Copy, Check, Wifi } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { v4 as uuidv4 } from "uuid";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "@/components/ui/use-toast";

const PublicScan = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [scannedId, setScannedId] = useState<string | null>(null);
  const [scannedQR, setScannedQR] = useState<string | null>(null);
  const [scannedFormat, setScannedFormat] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'nfc' | 'qr'>('qr');
  const [showQRDialog, setShowQRDialog] = useState(false);
  const [generatedQR, setGeneratedQR] = useState<string | null>(null);
  const [copiedNfc, setCopiedNfc] = useState(false);
  const [copiedQr, setCopiedQr] = useState(false);

  // Check for pending QR ID on mount and set QR tab as active
  useEffect(() => {
    const pendingQrId = localStorage.getItem('pendingQrId');
    if (pendingQrId) {
      setActiveTab('qr');
    }
  }, []);

  const handleNFCScan = (id: string) => {
    setScannedId(id);
    setScannedFormat('NFC');
  };

  const handleQRScan = (value: string, format?: string) => {
    setScannedQR(value);
    setScannedFormat(format || 'Unknown Format');
  };

  const handleGenerateQR = () => {
    const uuid = uuidv4();
    const qrUrl = `https://tivo.bio/scan/${uuid}`;
    setGeneratedQR(qrUrl);
    setShowQRDialog(true);
  };

  const handleDownloadQR = () => {
    if (!generatedQR) return;
    
    const svg = document.getElementById('generated-qr-code');
    if (!svg) return;
    
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    canvas.width = 200;
    canvas.height = 200;
    
    const svgData = new XMLSerializer().serializeToString(svg);
    const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
    
    const url = URL.createObjectURL(svgBlob);
    
    const img = new Image();
    img.onload = () => {
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      
      canvas.toBlob((blob) => {
        if (!blob) return;
        
        const downloadUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.download = `tivo-qr-${Date.now()}.png`;
        link.href = downloadUrl;
        link.click();
        
        URL.revokeObjectURL(downloadUrl);
      }, 'image/png');
      
      URL.revokeObjectURL(url);
    };
    img.src = url;
  };

  const handleCopyId = async (text: string, type: 'nfc' | 'qr') => {
    try {
      await navigator.clipboard.writeText(text);
      if (type === 'nfc') {
        setCopiedNfc(true);
        setTimeout(() => setCopiedNfc(false), 2000);
      } else {
        setCopiedQr(true);
        setTimeout(() => setCopiedQr(false), 2000);
      }
      toast({
        description: "Code copied to clipboard",
      });
    } catch (err) {
      toast({
        variant: "destructive",
        description: "Failed to copy code",
      });
    }
  };

  return (
    <AppLayout>
      <div className="space-y-6">
        <div className="flex items-center">
          <AppleButton 
            variant="secondary" 
            onClick={() => navigate("/auth")}
            className="hover:bg-white -ml-3 text-gray-600"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            {t('buttons.back')}
          </AppleButton>
        </div>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t('scanner.publicTitle')}</h1>
          <p className="text-sm text-gray-500 mt-1">
            {t('scanner.publicDescription')}
          </p>
        </div>

        <div className="flex flex-col items-center space-y-8">
          <div className="w-[130px] h-[130px]">
            <LottiePlayer
              src="https://lottie.host/a5bba61b-ca65-466e-885a-467856ae4456/VPzIzTB6RK.lottie"
              style={{ width: '100%', height: '100%' }}
            />
          </div>

          <div className="w-full max-w-sm grid grid-cols-2 gap-3">
            <AppleButton 
              variant="secondary"
              className={`w-full h-14 text-lg relative flex items-center justify-center transition-all ${
                activeTab === 'qr' 
                  ? 'border border-[#007AFF] bg-white text-[#007AFF] rounded-[20px]' 
                  : 'bg-gray-100 text-gray-900 rounded-[20px]'
              }`}
              onClick={() => setActiveTab('qr')}
            >
              <QrCode className={`h-5 w-5 mr-2 ${activeTab === 'qr' ? 'text-[#007AFF]' : 'text-gray-600'}`} />
              {t('scanner.qrScanner')}
            </AppleButton>
            <AppleButton 
              variant="secondary"
              className={`w-full h-14 text-lg relative flex items-center justify-center transition-all ${
                activeTab === 'nfc' 
                  ? 'border border-[#007AFF] bg-white text-[#007AFF] rounded-[20px]' 
                  : 'bg-gray-100 text-gray-900 rounded-[20px]'
              }`}
              onClick={() => setActiveTab('nfc')}
            >
              <Wifi className={`h-5 w-5 mr-2 ${activeTab === 'nfc' ? 'text-[#007AFF]' : 'text-gray-600'}`} />
              {t('scanner.nfcScanner')}
            </AppleButton>
          </div>

          <div className="w-full max-w-sm">
            {activeTab === 'nfc' && (
              <div className="flex flex-col items-center">
                <NFCScanner onScan={handleNFCScan} />
                {scannedId && (
                  <div className="w-full p-4 bg-gray-50 rounded-xl space-y-2">
                    <p className="text-sm text-gray-500 text-center">{t('scanner.scannedId.nfc')}:</p>
                    <div 
                      className="max-w-full overflow-hidden group cursor-pointer relative"
                      onClick={() => handleCopyId(scannedId, 'nfc')}
                    >
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 bg-gray-50/90 transition-opacity">
                        {copiedNfc ? (
                          <Check className="h-5 w-5 text-green-500" />
                        ) : (
                          <Copy className="h-5 w-5 text-gray-500" />
                        )}
                      </div>
                      <p className="font-mono text-sm text-center text-gray-500 mb-1">NFC</p>
                      <p className="font-mono text-sm text-center text-blue-600 break-all">{scannedId}</p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'qr' && (
              <div className="flex flex-col items-center">
                <QRScanner onScan={(value, format) => handleQRScan(value, format)} />
                {scannedQR && (
                  <div className="w-full p-4 bg-gray-50 rounded-xl space-y-2">
                    <p className="text-sm text-gray-500 text-center">{t('scanner.scannedId.qr')}:</p>
                    <div 
                      className="max-w-full overflow-hidden group cursor-pointer relative"
                      onClick={() => handleCopyId(scannedQR, 'qr')}
                    >
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 bg-gray-50/90 transition-opacity">
                        {copiedQr ? (
                          <Check className="h-5 w-5 text-green-500" />
                        ) : (
                          <Copy className="h-5 w-5 text-gray-500" />
                        )}
                      </div>
                      <p className="font-mono text-sm text-center text-gray-500 mb-1">{scannedFormat}</p>
                      <p className="font-mono text-sm text-center text-blue-600 break-all">{scannedQR}</p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Generate QR Button */}
          <div className="w-full max-w-sm">
            <AppleButton
              variant="secondary"
              className="w-full h-12 text-base bg-white hover:bg-gray-50 text-gray-600 border border-gray-100"
              onClick={handleGenerateQR}
            >
              <QrCode className="h-5 w-5 mr-2" />
              Generate QR Code
            </AppleButton>
          </div>

          {/* CTA Section */}
          <div className="mt-16 text-center max-w-sm w-full">
            <h3 className="text-2xl text-gray-700 font-medium mb-6">
              {t('scanner.readyToManage')}
            </h3>
            <p className="text-gray-600 mb-8">
              {t('scanner.createAccount')}
            </p>
            <AppleButton
              onClick={() => navigate("/auth")}
              variant="primary"
              className="w-full h-14 text-lg rounded-xl relative justify-center"
            >
              {t('buttons.getStarted')}
              <ArrowRight className="w-5 h-5 ml-2" />
            </AppleButton>
          </div>
        </div>
      </div>

      {/* QR Code Generation Dialog */}
      <Dialog open={showQRDialog} onOpenChange={setShowQRDialog}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Generated QR Code</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center space-y-4 p-4">
            {generatedQR && (
              <>
                <QRCodeSVG
                  id="generated-qr-code"
                  value={generatedQR}
                  size={200}
                  level="H"
                  includeMargin
                />
                <p className="text-sm text-gray-500 text-center break-all">
                  {generatedQR}
                </p>
                <AppleButton
                  variant="secondary"
                  size="sm"
                  onClick={handleDownloadQR}
                  className="mt-4"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download QR Code
                </AppleButton>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
};

export default PublicScan; 